import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, CheckCircle, XCircle, Slash, Calendar, MapPin, Briefcase, FileText, Upload, Download, RefreshCw, Send,
  Layers, Shield, DollarSign, Flag,
} from "react-feather";
import toast from "react-hot-toast";
import {
  useGetEnquiryQuery, useApproveEnquiryMutation, useSetEnquiryDiscountMutation, useRejectEnquiryMutation, useConfirmEnquiryMutation,
  useMarkEnquiryLostMutation, usePostponeEnquiryMutation, useReviveEnquiryMutation,
  useUploadQuotationMutation, useResendQuotationMutation, useLazyGetQuotationPdfContentQuery,
} from "../../salesApi";
import StatusBadge from "../../../../components/common/StatusBadge";
import ActorName from "../../../../components/common/ActorName";
import ReasonPrompt from "../../../../components/common/ReasonPrompt";
import CollapsibleCard, { PANEL_ACCENT } from "../../components/CollapsibleCard";
import { AmountTile, ServicePill, GuardRequirementsTable } from "../../components/PanelBits";
import DiscountFields, { DiscountedTotalsStrip, computeDiscountedTotals, discountRequest } from "../../components/DiscountFields";
import ServiceLinesGrid, {
  ServiceLinesTable, hasPricedLines, linesStateFrom, computeLinesSubTotal, toServiceLineRequests, validateServiceLines,
} from "../../components/ServiceLinesGrid";
import SalesQuotationsPanel from "../../components/SalesQuotationsPanel";
import SalesLinkedDocumentsPanel from "../../components/SalesLinkedDocumentsPanel";
import QuotationPreviewModal from "../../quotation/QuotationPreviewModal";
import { ENQUIRY_STATUS_CLASS, ENQUIRY_STATUS_LABEL, qualifiesForRateBasedApproval } from "../../salesStatusMaps";
import { useCanCreateEnquiry, useCanApproveEnquiry, useCanApproveWithExternalQuotation, useCanUploadQuotation, useCanSelfApprove, useCanViewAllSales } from "../../useSalesPermissions";
import { useClaims } from "../../../auth/useClaims";
import { paths } from "../../../../routes/paths";
// New Quotation module (src/Quotations) - separate from the legacy Sales-
// module quotation-document flow above (App_SalesQuotation). A Quotation can
// optionally reference this enquiry (SalesEnquiryRecordGuid); when one does,
// the approver should see it here before approving, and once it's APPROVED
// (whether by its own Approve action or automatically alongside this
// enquiry's own approval - see SalesService.AutoApproveLinkedQuotationAsync)
// it can be sent from either this page or the Quotation module's own page.
import { useListQuotationsQuery, useGetQuotationQuery } from "../../quotations/quotationsApi";
import { useCanViewQuotations, useCanSendQuotation as useCanSendNewQuotation } from "../../quotations/useQuotationPermissions";
import QuotationLinkedPanel from "../../quotations/components/QuotationLinkedPanel";
import QuotationDeliveryModal from "../../quotations/components/QuotationDeliveryModal";
import { buildQuotationPrintProps } from "../../quotations/buildQuotationPrintProps";
import { useGetCompanyInfoQuery } from "../../../settings/settingsApi";
import { useGetServiceTypesQuery } from "../../../masters/serviceTypes/serviceTypesApi";
import { useLazyGetGeneralManagerSignatureQuery } from "../../../masters/employees/employeesApi";
import { blobToDataUrl } from "../../../stock/lpo/buildLpoPrintProps";
import { DEFAULT_VAT_PERCENT } from "../../../../components/common/vatDefaults";

const money = (n) => (n == null ? "-" : Number(n).toFixed(2));

const EnquiryDetail = () => {
  const { recordGuid } = useParams();
  const navigate = useNavigate();
  const { userId } = useClaims();
  const canCreateEnquiry = useCanCreateEnquiry();
  const canViewAllSales = useCanViewAllSales();
  const canApproveEnquiry = useCanApproveEnquiry();
  const canApproveExternal = useCanApproveWithExternalQuotation();
  const canUploadQuotation = useCanUploadQuotation();
  const canSelfApprove = useCanSelfApprove();
  const canViewNewQuotation = useCanViewQuotations();
  const canSendNewQuotation = useCanSendNewQuotation();

  const { data: detail, isLoading, isError, error } = useGetEnquiryQuery(recordGuid);
  // Reverse lookup (the new Quotation module only stores the forward link,
  // Quotation.SalesEnquiryRecordGuid) - at most one is expected in practice,
  // per SalesService.AutoApproveLinkedQuotationAsync's own "most recently
  // created" assumption. Skipped entirely for a caller without Quotation.View
  // rather than letting the request 403.
  const { data: linkedQuotationsPage } = useListQuotationsQuery(
    { salesEnquiryRecordGuid: recordGuid, pageSize: 1 },
    { skip: !recordGuid || !canViewNewQuotation }
  );
  const linkedQuotationSummary = linkedQuotationsPage?.items?.[0];
  const { data: linkedQuotation } = useGetQuotationQuery(linkedQuotationSummary?.recordGuid, { skip: !linkedQuotationSummary });
  const { data: linkedQuotationCompanyInfo } = useGetCompanyInfoQuery(linkedQuotation?.branchId, { skip: !linkedQuotation });
  const { data: linkedQuotationBranchServiceTypes = [] } = useGetServiceTypesQuery(
    { branchId: linkedQuotation?.branchId, isActive: true },
    { skip: !linkedQuotation }
  );
  const [fetchLinkedQuotationSignature] = useLazyGetGeneralManagerSignatureQuery();
  const [linkedQuotationSignatureUrl, setLinkedQuotationSignatureUrl] = useState(null);
  const [showQuotationDeliveryModal, setShowQuotationDeliveryModal] = useState(false);
  const [approveEnquiry] = useApproveEnquiryMutation();
  const [setEnquiryDiscount, { isLoading: isSavingDiscount }] = useSetEnquiryDiscountMutation();
  const [rejectEnquiry] = useRejectEnquiryMutation();
  const [confirmEnquiry, { isLoading: isConfirming }] = useConfirmEnquiryMutation();
  const [markLost] = useMarkEnquiryLostMutation();
  const [postpone] = usePostponeEnquiryMutation();
  const [revive, { isLoading: isReviving }] = useReviveEnquiryMutation();
  const [uploadQuotation, { isLoading: isUploading }] = useUploadQuotationMutation();
  const [resendQuotation, { isLoading: isResending }] = useResendQuotationMutation();
  const [fetchQuotationPdf] = useLazyGetQuotationPdfContentQuery();

  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showLostForm, setShowLostForm] = useState(false);
  const [showPostponeForm, setShowPostponeForm] = useState(false);
  const [followUpDate, setFollowUpDate] = useState("");
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  // The approver's copy of the priced lines (Batch164) - same services as
  // requested, rates and Other quantities adjustable.
  const [approveLines, setApproveLines] = useState({});
  const [vatRate, setVatRate] = useState(String(DEFAULT_VAT_PERCENT));
  const [discountPercent, setDiscountPercent] = useState("");
  const [discountValue, setDiscountValue] = useState("");
  const [showApproveForm, setShowApproveForm] = useState(false);
  // Discount typed into the Approve form itself (Batch163) - separate from the
  // after-approval discount panel's own inputs above.
  const [approveDiscountPercent, setApproveDiscountPercent] = useState("");
  const [approveDiscountAmount, setApproveDiscountAmount] = useState("");

  // GENERAL MANAGER's seal, matching QuotationDetail (2026-09-09) - the same
  // document is generated from both screens, so both must stamp it the same way.
  useEffect(() => {
    let cancelled = false;
    fetchLinkedQuotationSignature()
      .unwrap()
      .then(blobToDataUrl)
      .then((url) => { if (!cancelled) setLinkedQuotationSignatureUrl(url); })
      .catch(() => { if (!cancelled) setLinkedQuotationSignatureUrl(null); });
    return () => { cancelled = true; };
  }, [fetchLinkedQuotationSignature]);

  if (isLoading) return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  if (isError || !detail) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.salesEnquiries} className="d-inline-flex align-items-center mb-3"><ArrowLeft size={16} className="me-1" /> Back to Enquiries</Link>
          <div className="alert alert-danger">Could not load this enquiry ({error?.message || "server error"}).</div>
        </div>
      </div>
    );
  }

  const { enquiry, guardRequirements = [], serviceTypes = [] } = detail;
  // Rows for the lines grid/table. serviceLines carries names and kinds; the
  // plain serviceTypes list is only a fallback for a response without it.
  const serviceLines = detail.serviceLines ?? serviceTypes.map((s) => ({ ...s, qty: null, rate: null, amount: null }));
  const linesArePriced = hasPricedLines(serviceLines);

  const handleSaveDiscount = async () => {
    const pct = discountPercent.trim() ? Number(discountPercent) : null;
    const amt = discountValue.trim() ? Number(discountValue) : null;
    if (pct == null && amt == null) return toast.error("Enter a discount percentage or an amount");
    if (pct != null && amt != null) return toast.error("Enter either a percentage or an amount, not both");
    try {
      await setEnquiryDiscount({ recordGuid, discountPercent: pct, discountAmount: amt }).unwrap();
      toast.success("Discount applied");
      // Cleared rather than left filled: the applied figures are now shown in
      // the summary above, so leaving the inputs populated would read as an
      // unsaved edit.
      setDiscountPercent("");
      setDiscountValue("");
    } catch (e) {
      toast.error(e?.message || "Could not apply the discount");
    }
  };

  const handleClearDiscount = async () => {
    if (!window.confirm("Remove the discount from this quotation?")) return;
    try {
      await setEnquiryDiscount({ recordGuid, discountPercent: null, discountAmount: null }).unwrap();
      toast.success("Discount removed");
      setDiscountPercent("");
      setDiscountValue("");
    } catch (e) {
      toast.error(e?.message || "Could not remove the discount");
    }
  };
  // This enquiry's own selected service types double as the linked
  // Quotation's "QUOTATION FOR ___" heading source (buildQuotationPrintProps'
  // enquiryServiceTypes param) - already loaded above, no second fetch needed.
  const linkedQuotationPrintProps = linkedQuotation
    ? buildQuotationPrintProps(linkedQuotation, linkedQuotationCompanyInfo, linkedQuotationSignatureUrl, linkedQuotationBranchServiceTypes, serviceTypes)
    : null;
  // By user id - names are display text and two people can share one.
  const isOwner = userId != null && enquiry.salespersonUserId === userId;
  const isSubmitted = enquiry.status === "SUBMITTED";
  // Still decides WHO may approve (rate-based vs external-quotation permission);
  // the pricing itself is now the lines, whichever path this is.
  const isRateBased = qualifiesForRateBasedApproval(serviceTypes, enquiry.branchCode);

  const canApproveNow = isSubmitted && (isOwner ? (isRateBased || canSelfApprove) : (isRateBased ? canApproveEnquiry : canApproveExternal));
  const canRejectNow = isSubmitted && (isOwner ? canSelfApprove : canApproveEnquiry);
  // Close-out (confirm / postpone / lost / revive): the owner, or since 2026-09-22 a manager
  // with Sales.ViewAll acting on the salesperson's behalf - the API applies the same rule.
  // Resubmit stays the owner's: it rewrites the proposal.
  const canCloseOutNow = isOwner ? canCreateEnquiry : canViewAllSales;
  const canConfirmNow = canCloseOutNow && enquiry.status === "APPROVED";
  const canLostOrPostponeNow = canCloseOutNow && enquiry.status === "APPROVED";
  const canReviveNow = canCloseOutNow && (enquiry.status === "POSTPONED" || enquiry.status === "LOST");
  const canResubmitNow = canCreateEnquiry && isOwner && (enquiry.status === "REJECTED" || enquiry.status === "APPROVED");
  const canUploadNow = canUploadQuotation && enquiry.status === "APPROVED";

  // Live preview of what Approve will save, discount included. Same inputs the
  // request below sends, so the figure shown is the figure stored.
  const approveSubTotal = computeLinesSubTotal(serviceLines, approveLines, enquiry.guardsCount || 0);
  const approveTotals = computeDiscountedTotals(approveSubTotal, vatRate, approveDiscountPercent, approveDiscountAmount);

  const toggleApproveForm = () => {
    // Opens prefilled with the salesperson's lines and any discount the
    // enquiry already carries, so approving never silently drops either.
    if (!showApproveForm) {
      const state = linesStateFrom(serviceLines);
      serviceLines.forEach((l) => {
        if (l.serviceKind === "ALARMS") state[l.serviceTypeId].qty = String(enquiry.alarmsCount || 0);
        if (l.serviceKind === "OTHER" && !state[l.serviceTypeId].qty) state[l.serviceTypeId].qty = "1";
      });
      setApproveLines(state);
      // An enquiry carries no VAT until it is approved (0 = not set yet), so the
      // approver starts from the standard 16%.
      setVatRate(enquiry.vatRate ? String(enquiry.vatRate) : String(DEFAULT_VAT_PERCENT));
      setApproveDiscountPercent(enquiry.discountPercent ? String(enquiry.discountPercent) : "");
      setApproveDiscountAmount(!enquiry.discountPercent && enquiry.discountAmount ? String(enquiry.discountAmount) : "");
    }
    setShowApproveForm((s) => !s);
  };

  const handleApprove = async () => {
    const linesError = validateServiceLines(serviceLines, approveLines, enquiry.guardsCount || 0, { requireRates: true, requireGuardLines: false });
    if (linesError) return toast.error(linesError);
    if (approveTotals.exceeds) return toast.error("The discount is more than the sub total");
    const discount = discountRequest(approveDiscountPercent, approveDiscountAmount);
    try {
      await approveEnquiry({
        // Always explicit (0 = none): the form opened prefilled, so a blank
        // box means the approver removed the discount, not "leave it".
        discountPercent: discount.discountPercent ?? 0,
        discountAmount: discount.discountAmount ?? 0,
        recordGuid,
        // The lines are the pricing; the server derives the per-kind figures.
        serviceLines: toServiceLineRequests(serviceLines, approveLines, enquiry.guardsCount || 0),
        tempGuardsAmount: 0,
        vatRate: Number(vatRate) || 0,
      }).unwrap();
      toast.success("Enquiry approved");
      setShowApproveForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not approve the enquiry");
    }
  };

  const handleReject = async (rejectionReason) => {
    try {
      await rejectEnquiry({ recordGuid, rejectionReason }).unwrap();
      toast.success("Enquiry rejected");
      setShowRejectForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not reject the enquiry");
    }
  };

  const handleConfirm = async () => {
    if (!window.confirm("Confirm this enquiry into a Sales Order?")) return;
    try {
      const order = await confirmEnquiry(recordGuid).unwrap();
      toast.success("Enquiry confirmed");
      navigate(paths.salesOrderDetail(order.recordGuid));
    } catch (e) {
      toast.error(e?.message || "Could not confirm the enquiry");
    }
  };

  const handleLost = async (reason) => {
    try {
      await markLost({ recordGuid, reason }).unwrap();
      toast.success("Enquiry marked lost");
      setShowLostForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not mark the enquiry lost");
    }
  };

  const handlePostpone = async (remark) => {
    try {
      await postpone({ recordGuid, remark, followUpDate: followUpDate || undefined }).unwrap();
      toast.success("Enquiry postponed");
      setShowPostponeForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not postpone the enquiry");
    }
  };

  const handleRevive = async () => {
    try {
      await revive(recordGuid).unwrap();
      toast.success("Enquiry revived");
    } catch (e) {
      toast.error(e?.message || "Could not revive the enquiry");
    }
  };

  const handleAttachFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await uploadQuotation({ recordGuid, file }).unwrap();
      toast.success("Quotation attached and sent");
    } catch (err) {
      toast.error(err?.message || "Could not attach the quotation");
    }
  };

  const handleViewQuotation = async () => {
    try {
      const blob = await fetchQuotationPdf(recordGuid).unwrap();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e?.message || "Could not open the quotation");
    }
  };

  const handleResend = async () => {
    try {
      await resendQuotation(recordGuid).unwrap();
      toast.success("Delivery re-attempted");
    } catch (e) {
      toast.error(e?.message || "Could not resend the quotation");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.salesEnquiries} className="d-inline-flex align-items-center mb-2"><ArrowLeft size={16} className="me-1" /> Back to Enquiries</Link>
              <h4>{enquiry.enquiryNumber} <StatusBadge status={enquiry.status} classMap={ENQUIRY_STATUS_CLASS} labelMap={ENQUIRY_STATUS_LABEL} /></h4>
              <h6>{enquiry.branchCode}</h6>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body d-flex align-items-center gap-3">
            <span className="master-detail-avatar master-detail-avatar-lg">{(enquiry.customerName || "?").trim().slice(0, 2).toUpperCase()}</span>
            <div className="flex-grow-1">
              <h5 className="mb-0">{enquiry.customerName}</h5>
              <div className="d-flex flex-wrap gap-3 mt-2 text-muted small">
                <span className="d-inline-flex align-items-center"><Briefcase size={14} className="me-1" />{enquiry.salespersonName}</span>
                <span className="d-inline-flex align-items-center"><Calendar size={14} className="me-1" />{new Date(enquiry.createdDate).toLocaleDateString()}</span>
                {enquiry.area && <span className="d-inline-flex align-items-center"><MapPin size={14} className="me-1" />{enquiry.area}</span>}
                {enquiry.totalAmount != null && <span>Total: <strong>{money(enquiry.totalAmount)}</strong></span>}
              </div>
            </div>
          </div>
        </div>

        {enquiry.status === "REJECTED" && enquiry.rejectionReason && (
          <div className="alert alert-danger">Rejected by {enquiry.rejectedByName}: {enquiry.rejectionReason}</div>
        )}
        {enquiry.status === "LOST" && <div className="alert alert-secondary">Lost{enquiry.lostReason ? `: ${enquiry.lostReason}` : ""}</div>}
        {enquiry.status === "POSTPONED" && (
          <div className="alert alert-secondary">
            Postponed{enquiry.postponedReason ? `: ${enquiry.postponedReason}` : ""}
            {enquiry.followUpDate && ` - follow up on ${new Date(enquiry.followUpDate).toLocaleDateString()}`}
          </div>
        )}

        {serviceTypes.length > 0 && (
          <CollapsibleCard
            title="Services & Pricing"
            icon={Layers}
            accent={PANEL_ACCENT.services}
            count={serviceTypes.length}
            summary={isSubmitted && linesArePriced ? "proposed by the salesperson" : null}
          >
            {linesArePriced ? (
              <ServiceLinesTable lines={serviceLines} />
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {serviceTypes.map((s) => (
                  <ServicePill key={s.serviceTypeId} name={s.serviceTypeName} kind={s.serviceKind} />
                ))}
              </div>
            )}
            {!linesArePriced && enquiry.otherServicesAmount > 0 && (
              <div
                className="mt-3 d-inline-block px-3 py-2"
                style={{ border: "1.5px solid #6E1423", borderRadius: 8, background: "#FBEEEF" }}
              >
                <div className="text-muted small text-uppercase" style={{ letterSpacing: 0.5 }}>
                  {enquiry.status === "SUBMITTED" ? "Amount Requested" : "Amount"}
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: "#6E1423", lineHeight: 1.2 }}>
                  {money(enquiry.otherServicesAmount)}
                </div>
              </div>
            )}
          </CollapsibleCard>
        )}

        {guardRequirements.length > 0 && (
          <CollapsibleCard
            title="Guard Requirements"
            icon={Shield}
            accent={PANEL_ACCENT.guards}
            count={enquiry.guardsCount}
            summary={`guard${enquiry.guardsCount === 1 ? "" : "s"} · ${guardRequirements.length} line${guardRequirements.length === 1 ? "" : "s"}`}
          >
            {/* Dates ride inside the Deployment chip (Permanent · from X /
                Temporary · X – Y); the guard total is the header's count pill. */}
            <GuardRequirementsTable lines={guardRequirements} />
          </CollapsibleCard>
        )}

        <SalesQuotationsPanel
          enquiryRecordGuid={recordGuid}
          branchId={enquiry.branchId}
          custId={enquiry.custId}
          customerBranchId={enquiry.branchId}
          customerName={enquiry.customerName}
          reference={{ key: `enquiry-${recordGuid}`, salesEnquiryRecordGuid: recordGuid, salesReferenceNumber: enquiry.enquiryNumber }}
          serviceLines={serviceLines}
          vatRate={enquiry.vatRate}
        />

        {/* The order it became, quotations, LPOs, stock-outs, technical work
            and guard assignments - each opens the real record (Batch166). */}
        <SalesLinkedDocumentsPanel enquiryRecordGuid={recordGuid} hideKind="SALES_ENQUIRY" />

        {linkedQuotation && (
          <>
            <QuotationLinkedPanel quotation={linkedQuotation} />
            {linkedQuotation.status === "APPROVED" && canSendNewQuotation && (
              <div className="mb-3">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                  onClick={() => setShowQuotationDeliveryModal(true)}
                >
                  <Send size={14} /> Generate &amp; Send Quotation
                </button>
              </div>
            )}
          </>
        )}

        <CollapsibleCard
          title="Pricing / Approval"
          icon={DollarSign}
          accent={PANEL_ACCENT.money}
          hasData={enquiry.totalAmount != null}
          // An unpriced enquiry still opens - the Approve button lives here.
          defaultOpen
          forceOpen={showApproveForm || showRejectForm}
          summary={enquiry.totalAmount != null ? `Total ${money(enquiry.totalAmount)}${enquiry.discountAmount > 0 ? " · discounted" : ""}` : null}
          actions={
            <>
              {canApproveNow && (
                <button type="button" className="btn btn-success btn-sm d-inline-flex align-items-center gap-1" onClick={toggleApproveForm}>
                  <CheckCircle size={14} /> Approve
                </button>
              )}
              {canRejectNow && (
                <button type="button" className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1" onClick={() => setShowRejectForm((s) => !s)}>
                  <XCircle size={14} /> Reject
                </button>
              )}
            </>
          }
        >
          {showRejectForm && <ReasonPrompt label="Rejection Reason" confirmLabel="Reject" onConfirm={handleReject} onCancel={() => setShowRejectForm(false)} />}
          {showApproveForm && (
            <div className="card mb-3">
              <div className="card-body">
                {/* The salesperson's lines, prefilled. Services and the Guards /
                    Alarms quantities are fixed by the enquiry itself - changing
                    those is a resubmit; the rates and Other quantities are the
                    approver's to adjust. */}
                <div className="mb-3">
                  <ServiceLinesGrid
                    serviceTypes={serviceLines}
                    value={approveLines}
                    onChange={setApproveLines}
                    guardsQty={enquiry.guardsCount || 0}
                    alarmsQtyLocked
                    selectionLocked
                  />
                </div>
                <div className="row">
                  <div className="col-md-3 mb-3"><label className="form-label">VAT %</label><input type="number" className="form-control" value={vatRate} onChange={(e) => setVatRate(e.target.value)} /></div>
                </div>
                <div className="row">
                  <DiscountFields
                    percent={approveDiscountPercent}
                    amount={approveDiscountAmount}
                    onPercentChange={setApproveDiscountPercent}
                    onAmountChange={setApproveDiscountAmount}
                  />
                </div>
                <div className="mb-3">
                  <DiscountedTotalsStrip totals={approveTotals} vatLabel={`VAT (${Number(vatRate) || 0}%)`} percent={approveDiscountPercent} />
                </div>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-success" onClick={handleApprove}>Confirm Approval</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowApproveForm(false)}>Cancel</button>
                </div>
              </div>
            </div>
          )}
          {enquiry.totalAmount == null ? (
            <div className="text-muted small">Not yet priced.</div>
          ) : (
            <>
              {isSubmitted && (
                <div className="small mb-2" style={{ color: "#B54708" }}>
                  Proposed figures - not approved yet. The approver can adjust the rates.
                </div>
              )}
              {/* Per-kind tiles only for older enquiries - priced lines already
                  show every service's working in the panel above. */}
              <div className={`d-flex flex-wrap gap-2 ${linesArePriced ? "" : "mb-3"}`}>
                {!linesArePriced && enquiry.guardsAmount > 0 && (
                  <AmountTile label="Guards" amount={enquiry.guardsAmount} color={PANEL_ACCENT.guards}
                    sub={enquiry.guardsRate > 0 ? `${enquiry.guardsCount} × ${money(enquiry.guardsRate)}/guard` : null} />
                )}
                {!linesArePriced && enquiry.alarmsAmount > 0 && (
                  <AmountTile label="Alarms" amount={enquiry.alarmsAmount} color={PANEL_ACCENT.services}
                    sub={enquiry.alarmsRate > 0 ? `${enquiry.alarmsCount} × ${money(enquiry.alarmsRate)}` : null} />
                )}
                {!linesArePriced && enquiry.otherServicesAmount > 0 && (
                  <AmountTile label="Other Services" amount={enquiry.otherServicesAmount} color={PANEL_ACCENT.work} />
                )}
              </div>
              {/* Sub Total here is the GROSS figure before any discount. The
                  stored TotalAmount/VatAmount are already net of it, so the
                  discount has to be added back to reconstruct the original -
                  otherwise the line would silently read as the discounted
                  amount and the Discount row below would double-count. */}
              <div
                className="d-flex flex-wrap align-items-center gap-4 px-3 py-2 rounded"
                style={{ background: "#EEF7F1", border: "1px solid #CDE8D6" }}
              >
                <span>Sub Total: <strong>{money(enquiry.totalAmount - enquiry.vatAmount + (enquiry.discountAmount || 0))}</strong></span>
                {enquiry.discountAmount > 0 && (
                  <>
                    <span className="text-danger">
                      Discount{enquiry.discountPercent ? ` (${enquiry.discountPercent}%)` : ""}:{" "}
                      <strong>-{money(enquiry.discountAmount)}</strong>
                    </span>
                    <span>Net: <strong>{money(enquiry.totalAmount - enquiry.vatAmount)}</strong></span>
                  </>
                )}
                <span>VAT ({enquiry.vatRate || 0}%): <strong>{money(enquiry.vatAmount)}</strong></span>
                <span className="ms-auto" style={{ color: PANEL_ACCENT.money }}>
                  Total (incl. VAT): <strong style={{ fontSize: 18 }}>{money(enquiry.totalAmount)}</strong>
                </span>
              </div>

              {/* Editable until CONFIRMED - the server refuses after that, and
                  the same Sales.ApproveEnquiry that sets the price governs
                  discounting it. */}
              {canApproveEnquiry && enquiry.status !== "CONFIRMED" && (
                <div className="mt-3 p-2 rounded" style={{ background: "#F8F9FA", border: "1px solid #E9ECEF" }}>
                  <div className="row g-2 align-items-end">
                    <div className="col-md-3">
                      <label className="form-label mb-1 small">Discount %</label>
                      <input
                        type="number" min="0" max="100" step="0.01"
                        className="form-control form-control-sm"
                        value={discountPercent}
                        placeholder="e.g. 10"
                        onChange={(e) => { setDiscountPercent(e.target.value); if (e.target.value) setDiscountValue(""); }}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label mb-1 small">or Discount Amount</label>
                      <input
                        type="number" min="0" step="0.01"
                        className="form-control form-control-sm"
                        value={discountValue}
                        placeholder="e.g. 500"
                        onChange={(e) => { setDiscountValue(e.target.value); if (e.target.value) setDiscountPercent(""); }}
                      />
                    </div>
                    <div className="col-md-6 d-flex gap-2">
                      <button type="button" className="btn btn-sm btn-primary" disabled={isSavingDiscount} onClick={handleSaveDiscount}>
                        {isSavingDiscount ? "Saving..." : enquiry.discountAmount > 0 ? "Update Discount" : "Apply Discount"}
                      </button>
                      {enquiry.discountAmount > 0 && (
                        <button type="button" className="btn btn-sm btn-outline-danger" disabled={isSavingDiscount} onClick={handleClearDiscount}>
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <small className="text-muted">
                    Enter a percentage or a flat amount, not both. VAT is charged on the discounted figure.
                  </small>
                </div>
              )}
            </>
          )}
          {enquiry.approvedByName && (
            <div className="text-muted small mt-2">Approved by <ActorName action="Approved" name={enquiry.approvedByName} /> on {new Date(enquiry.approvedDate).toLocaleString()}</div>
          )}
        </CollapsibleCard>

        {(enquiry.status === "APPROVED" || enquiry.status === "CONFIRMED") && (
          <CollapsibleCard
            title="Quotation"
            icon={FileText}
            accent={PANEL_ACCENT.document}
            hasData={enquiry.currentQuotationId != null}
            // Opens even when empty - Generate / Attach live in this header.
            defaultOpen
            summary={enquiry.currentQuotationId != null ? "Quotation on file" : null}
            actions={
              canUploadNow && (
                <>
                  <button type="button" className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1" onClick={() => setShowQuotationModal(true)}>
                    <Send size={14} /> Generate &amp; Send
                  </button>
                  <label className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1 mb-0">
                    <Upload size={14} /> {isUploading ? "Uploading..." : "Attach PDF"}
                    <input type="file" accept="application/pdf" hidden onChange={handleAttachFile} disabled={isUploading} />
                  </label>
                </>
              )
            }
          >
            <div className="d-flex flex-wrap gap-3 align-items-center">
              <button type="button" className="btn btn-link p-0 d-inline-flex align-items-center gap-1" onClick={handleViewQuotation}>
                <Download size={14} /> View Quotation
              </button>
              <button type="button" className="btn btn-link p-0 d-inline-flex align-items-center gap-1" onClick={handleResend} disabled={isResending}>
                <RefreshCw size={14} /> {isResending ? "Resending..." : "Resend"}
              </button>
            </div>
          </CollapsibleCard>
        )}

        {(canConfirmNow || canLostOrPostponeNow || canReviveNow || canResubmitNow) && (
          <CollapsibleCard title="Close-out Actions" icon={Flag} accent={PANEL_ACCENT.neutral}>
            {showLostForm && <ReasonPrompt label="Lost Reason" confirmLabel="Mark Lost" onConfirm={handleLost} onCancel={() => setShowLostForm(false)} />}
            {showPostponeForm && (
              <div className="mb-2">
                <label className="form-label">Follow-up Date (optional)</label>
                <input type="date" className="form-control mb-2" style={{ maxWidth: 220 }} value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} />
                <ReasonPrompt label="Postpone Remark" confirmLabel="Postpone" confirmVariant="secondary" onConfirm={handlePostpone} onCancel={() => setShowPostponeForm(false)} />
              </div>
            )}
            <div className="d-flex gap-2 flex-wrap">
              {canConfirmNow && (
                <button type="button" className="btn btn-added" onClick={handleConfirm} disabled={isConfirming}>Confirm Order</button>
              )}
              {canLostOrPostponeNow && (
                <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setShowLostForm((s) => !s)}>
                  <Slash size={14} /> Mark Lost
                </button>
              )}
              {canLostOrPostponeNow && (
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPostponeForm((s) => !s)}>Postpone</button>
              )}
              {canReviveNow && (
                <button type="button" className="btn btn-outline-primary" onClick={handleRevive} disabled={isReviving}>Revive</button>
              )}
              {canResubmitNow && (
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(paths.salesEnquiryResubmit(recordGuid))}>Edit &amp; Resubmit</button>
              )}
            </div>
          </CollapsibleCard>
        )}

        {enquiry.salespersonRemarks && (
          <div className="text-muted small mb-4">Remarks: {enquiry.salespersonRemarks}</div>
        )}

        <QuotationPreviewModal
          show={showQuotationModal}
          onClose={() => setShowQuotationModal(false)}
          enquiry={enquiry}
          guardRequirements={guardRequirements}
          serviceTypes={serviceTypes}
        />

        {linkedQuotation && (
          <QuotationDeliveryModal
            show={showQuotationDeliveryModal}
            onClose={() => setShowQuotationDeliveryModal(false)}
            docProps={linkedQuotationPrintProps}
            quotation={linkedQuotation}
          />
        )}
      </div>
    </div>
  );
};

export default EnquiryDetail;
